// Frame index: maps frame numbers to exact PTS (presentation timestamps) from the MP4 container.
// Eliminates all FPS-based frame↔time math by using the actual timestamps from the sample table.
// Falls back to FPS-based computation when the index isn't available.

import { createFile as createMP4BoxFile } from 'mp4box';
import { getFPS } from './constants';

let _frameTimestamps = null; // Array of PTS in seconds, indexed by frame number
let _fps = null;             // Actual FPS derived from the frame index

export const isFrameIndexReady = () => _frameTimestamps !== null;
export const getFrameCount = () => _frameTimestamps ? _frameTimestamps.length : 0;

// Get the exact PTS for a given frame number
export const getTimeForFrame = (frame) => {
  if (_frameTimestamps && frame >= 0 && frame < _frameTimestamps.length) {
    return _frameTimestamps[frame];
  }
  // Fallback to FPS-based calculation
  const fps = getFPS();
  return fps ? frame / fps : 0;
};

// Get the frame number for a given time (binary search the frame index)
export const getFrameForTime = (time) => {
  if (_frameTimestamps && _frameTimestamps.length > 0) {
    // Binary search: find the last frame whose PTS is <= time (+ small epsilon)
    let lo = 0, hi = _frameTimestamps.length - 1;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      if (_frameTimestamps[mid] <= time + 0.0001) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }
    return lo;
  }
  // Fallback to FPS-based calculation
  const fps = getFPS();
  return fps ? Math.floor(time * fps + 0.0001) : 0;
};

// Get frame duration at a given frame (useful for tolerance calculations)
export const getFrameDuration = (frame) => {
  if (_frameTimestamps && frame >= 0 && frame < _frameTimestamps.length - 1) {
    return _frameTimestamps[frame + 1] - _frameTimestamps[frame];
  }
  const fps = getFPS();
  return fps ? 1 / fps : 0;
};

// Get half-frame tolerance at a given frame
export const getFrameToleranceAtFrame = (frame) => {
  return getFrameDuration(frame) / 2;
};

// Get the actual FPS derived from the frame index
export const getIndexFPS = () => _fps;

// Clear the frame index (e.g., when loading a new video)
export const clearFrameIndex = () => {
  _frameTimestamps = null;
  _fps = null;
};

// Set frame data from an external source (e.g. WebCodecsPlayer).
// This allows the WebCodecsPlayer to share its frame index with all
// existing consumers of this module without them needing to change.
export const setFrameData = (timestamps, fps) => {
  _frameTimestamps = timestamps;
  _fps = fps;
};

// Build the frame index from a video source (File, Blob, or URL string).
// Parses the MP4 container's sample table to extract exact PTS for every frame.
export const buildFrameIndex = (source) => {
  return new Promise(async (resolve, reject) => {
    try {
      const mp4boxFile = createMP4BoxFile();

      mp4boxFile.onReady = (info) => {
        const videoTrack = info.tracks.find(t => t.type === 'video');
        if (!videoTrack) {
          console.warn('Frame index: no video track found');
          resolve(false);
          return;
        }

        // getTrackSamplesInfo returns metadata for all samples (no video data loaded)
        const samplesInfo = mp4boxFile.getTrackSamplesInfo(videoTrack.id);
        if (!samplesInfo || samplesInfo.length === 0) {
          console.warn('Frame index: no samples found in video track');
          resolve(false);
          return;
        }

        // Build the frame index: array of PTS in seconds
        // For videos without B-frames, CTS == DTS (monotonically increasing)
        // For videos with B-frames, CTS may be reordered — sort by CTS for presentation order
        const timescale = videoTrack.timescale;
        const rawTimestamps = samplesInfo.map(s => s.cts / timescale);

        // Sort by PTS to ensure presentation order (handles B-frame reordering)
        rawTimestamps.sort((a, b) => a - b);

        _frameTimestamps = rawTimestamps;

        // Derive actual FPS from the frame index
        if (_frameTimestamps.length >= 2) {
          const totalDuration = _frameTimestamps[_frameTimestamps.length - 1] - _frameTimestamps[0];
          _fps = (rawTimestamps.length - 1) / totalDuration;
        }

        console.log(
          `Frame index built: ${_frameTimestamps.length} frames, ` +
          `derived FPS: ${_fps?.toFixed(4)}, ` +
          `first PTS: ${_frameTimestamps[0]?.toFixed(6)}s, ` +
          `last PTS: ${_frameTimestamps[_frameTimestamps.length - 1]?.toFixed(6)}s`
        );

        resolve(true);
      };

      mp4boxFile.onError = (e) => {
        console.warn('Frame index: mp4box error:', e);
        resolve(false);
      };

      // Read the source data
      let buffer;
      if (source instanceof File || source instanceof Blob) {
        buffer = await source.arrayBuffer();
      } else if (typeof source === 'string') {
        // URL (including blob: URLs) — fetch it
        // Only fetch enough for the moov atom. For most MP4s the moov is at the start
        // or we need the full file. Fetch the whole thing for reliability.
        const response = await fetch(source);
        buffer = await response.arrayBuffer();
      } else {
        console.warn('Frame index: unsupported source type');
        resolve(false);
        return;
      }

      // mp4box.js requires fileStart property on the buffer
      buffer.fileStart = 0;
      mp4boxFile.appendBuffer(buffer);
      mp4boxFile.flush();

    } catch (error) {
      console.error('Frame index: failed to build:', error);
      resolve(false);
    }
  });
};
